"use client";

import { SetStateAction, useEffect, useRef, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { animate, stagger } from "animejs";

import AnimalCard from "./animal-card";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormValues } from "@/models/Sila.Animals.schema.ts";

export default function AnimalGrid({ Animals }: { Animals: FormValues[] }) {
  const [animals, setAnimals] = useState<FormValues[]>([]);
  const [filteredAnimals, setFilteredAnimals] = useState<FormValues[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [filterBy, setFilterBy] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const getAnimals = async () => {
      setIsLoading(true);
      try {
        // const data = await fetchAnimals ()
        setAnimals(Animals);
        setFilteredAnimals(Animals);
      } catch (error) {
        console.error("Error fetching animals:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getAnimals();
  }, [Animals]);

  useEffect(() => {
    // Filter and sort animals when dependencies change
    let result: FormValues[] = [...animals];

    if (!animals) {
    }

    // Apply search filter
    if (searchTerm) {
            result = result.filter(
        (animal) =>
          animal?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          animal?.dgr?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Apply category filter
    if (filterBy !== "all") {
      result = result.filter((animal) => {
        if (filterBy === "male") return animal.sex === "Male";
        if (filterBy === "female") return animal.sex === "Female";
        if (filterBy === "young") return (animal.age ?? 0) < 2;
        if (filterBy === "adult") return (animal.age ?? 0) >= 2;

        return true;
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "age") return (a.age ?? 0) - (b.age ?? 0);
      if (sortBy === "weight") return (a.weight ?? 0) - (b.weight ?? 0);
      const getTimestamp = (date?: string) =>
        date ? new Date(date).getTime() : -Infinity;

      return getTimestamp(b.birthDate) - getTimestamp(a.birthDate);

      // if ( sortBy === "birthDate" ) return new Date (
      // b.birthDate ) - new Date ( a.birthDate )
      // return 0
    });

    setFilteredAnimals(result);

    // Animate the grid items when they change
    if (gridRef.current && !isLoading) {
      const gridItems = gridRef.current.querySelectorAll(".animal-card");

      animate(gridItems, {
        opacity: [0, 1],
        translateY: [20, 0],
        delay: stagger(50),
        easing: "easeOutExpo",
        duration: 800,
      });
    }
  }, [animals, searchTerm, sortBy, filterBy, isLoading]);

  const handleSearch = (e: { target: { value: SetStateAction<string> } }) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="space-y-2 font-[suse]">
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="relative w-full md:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
            <Select value={filterBy} onValueChange={setFilterBy}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Filter by" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white">
                <SelectItem
                  value="all"
                  className={"overflow-y-auto z-50 background-white"}
                >
                  All Animals
                </SelectItem>
                <SelectItem
                  value="male"
                  className={"overflow-y-auto z-50 background-white"}
                >
                  Male
                </SelectItem>
                <SelectItem
                  value="female"
                  className={"overflow-y-auto z-50 background-white"}
                >
                  Female
                </SelectItem>
                <SelectItem
                  value="young"
                  className={"overflow-y-auto z-50 background-white"}
                >
                  Young (&lt;2y)
                </SelectItem>
                <SelectItem
                  value="adult"
                  className={"overflow-y-auto z-50 background-white"}
                >
                  Adult (≥2y)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white">
              <SelectItem
                value="name"
                className={"overflow-y-auto z-50 background-white"}
              >
                Name
              </SelectItem>
              <SelectItem
                value="age"
                className={"overflow-y-auto z-50 background-white"}
              >
                Age
              </SelectItem>
              <SelectItem
                value="weight"
                className={"overflow-y-auto z-50 background-white"}
              >
                Weight
              </SelectItem>
              <SelectItem
                value="birthDate"
                className={"overflow-y-auto z-50 background-white"}
              >
                Birth Date
              </SelectItem>
            </SelectContent>
          </Select>

          <div className="w-[130px]">
            <Button
              className={`w-1/2 rounded-r-none]`}
              variant={viewMode === "grid" ? "default" : "outline"}
              onClick={() => setViewMode("grid")}
            >
              Grid
            </Button>
            <Button
              className="w-1/2 rounded-l-none ]"
              variant={viewMode === "list" ? "default" : "outline"}
              onClick={() => setViewMode("list")}
            >
              List
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-6 mt-8 animate-pulse">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-64 bg-muted rounded-lg" />
          ))}
        </div>
      ) : (
        <div ref={gridRef}>
          {filteredAnimals.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                No animals found matching your criteria.
              </p>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setFilterBy("all");
                }}
              >
                Clear filters
              </Button>
            </div>
          ) : viewMode === "grid" ? (
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {filteredAnimals.map((animal) => (
                <AnimalCard
                  key={animal.dgr}
                  animal={animal}
                  className="animal-card"
                />
              ))}
            </div>
          ) : (
            <div className="space-y-4 mt-6">
              {filteredAnimals.map((animal) => (
                <AnimalCard
                  key={animal.dgr}
                  animal={animal}
                  className="animal-card"
                  layout="list"
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="text-sm text-muted-foreground mt-4">
        Showing {filteredAnimals.length} of {animals.length} animals
      </div>
    </div>
  );
}
